/**
 * Package-aware resolution.
 *
 * Wraps the existing single-file resolver with cross-package import logic:
 *
 *   - Each package has its own resolved kind registry.
 *   - `use X from foo.bar` in a file or in `package.archspace` adds the
 *     type X (resolved in foo.bar's registry) to this package's registry
 *     under its imported name (or alias).
 *   - Visibility scope follows the source: a `use` in `package.archspace`
 *     is visible to every `.arch` file in the package; a `use` in a `.arch`
 *     file is visible only inside that file. `TYPE_NOT_VISIBLE_IN_FILE`
 *     surfaces cross-file leakage after resolution.
 *   - `use * from foo.bar` adds every type that foo.bar `export`s.
 *   - `export use ... from foo.bar` re-exports the imported names so that
 *     wildcard-importers of *this* package see them too.
 *   - Std lib (`arch.*`) is implicit — no `dependencies` entry needed.
 *
 * The function is structured so a caller (LSP, CLI) can resolve a tree
 * of packages: resolve dependencies first, then pass the map of
 * already-resolved deps in. Cycles are detected by the caller.
 */

import type {
  Body,
  Diagnostic,
  DiagnosticSeverity,
  DoStep,
  PackageManifest,
  ParsedFile,
  ProcessStep,
  QualifiedName,
  SimpleStep,
  SourceSpan,
  TopLevelDecl,
  TypeDecl,
  UseDecl,
  Value,
} from "./types.js";
import type { DiagnosticCode } from "./diagnostics/codes.js";
import { buildSpliceScope } from "./splice-scope.js";
import { forEachCallHop } from "./process-ir-walk.js";
import type { Flow } from "./process-model.js";
import {
  resolveFile,
  type ResolvedType,
  type ResolvedModel,
  type ResolvedModule,
  type ResolvedInterface,
  type ResolvedSurface,
} from "./resolver.js";
import { qualifyWidgetTags } from "./widget-namespace.js";
import { spaceContains } from "./spaces.js";
import { documentDiagnostics } from "./validation.js";
import {
  buildScopedModuleIndex,
  ifaceSuffixes,
  resolveScoped,
  type ScopedCand,
} from "./hoist-index.js";
import type { MarkdownDocument } from "./markdown/document.js";
import { IncrementalCompiler, type ParseFn } from "./incremental.js";
import type { DependencyGraph } from "./graph.js";

function diag(
  severity: DiagnosticSeverity,
  message: string,
  source: SourceSpan | undefined,
  code: DiagnosticCode
): Diagnostic {
  return { severity, message, source, code };
}

// =============================================================================
// Public types
// =============================================================================

export interface PackageInput {
  /** Resolved package name (`arch.web.micro`). For anonymous packages, undefined. */
  readonly name?: QualifiedName;
  /** Parsed manifest, if a `package.archspace` exists. */
  readonly manifest?: PackageManifest;
  /** Every `.arch` file in the package, parsed. */
  readonly files: readonly ParsedFile[];
  /** Filesystem path to the package root (for diagnostics). */
  readonly rootPath?: string;
  /**
   * File URI → enclosing space name (v0.5). A file under a nested `name:`
   * manifest's subtree maps to that space; files not in any space are
   * absent. Drives space-scoped resolution and cross-space visibility.
   */
  readonly spaces?: ReadonlyMap<string, QualifiedName>;
  /**
   * `use … from <pkg>` declarations from nested **space** manifests (a
   * `package.archspace` carrying `name:`, v0.5). Each entry pairs the space's
   * qualified name with its imports. Unlike a root-manifest `use` (package-wide)
   * or a file `use` (file-scoped), a space `use` is **space-scoped**: the
   * imported names are visible to every `.arch` file in that space's subtree,
   * and to no other space (PACKAGE-ARCHSPACE.md §use). The loader collects these;
   * the resolver scopes them to the subtree's file URIs.
   */
  readonly spaceUses?: readonly SpaceScopedUses[];
  /** Markdown documents discovered alongside the `.arch` files (§4.15). */
  readonly documents?: readonly MarkdownDocument[];
}

/** A space manifest's imports, tagged with the space they scope to (§4.11). */
export interface SpaceScopedUses {
  /** The declaring space's qualified name (`name:` in the space manifest). */
  readonly space: QualifiedName;
  /** The `use … from …` declarations written in that space manifest. */
  readonly uses: readonly UseDecl[];
}

export interface ResolvedPackage {
  readonly input: PackageInput;
  /** The resolved model for this package alone (no imported types). */
  readonly model: ResolvedModel;
  /** Diagnostics from resolution + imports. */
  readonly diagnostics: readonly Diagnostic[];
  /**
   * Types this package exports (locally declared with `export`, plus types
   * re-exported via `export use`). Used by wildcard imports from
   * dependent packages.
   */
  readonly exports: ReadonlyMap<string, ResolvedType>;
  /** Imports applied while resolving this package — name → source info. */
  readonly imports: ReadonlyMap<string, ImportEntry>;
}

export interface ImportEntry {
  /** The local name (alias if renamed, original otherwise). */
  readonly localName: string;
  /** The original type's name in the source package. */
  readonly originalName: string;
  /** The source package's qualified name. */
  readonly fromPackage: QualifiedName;
  /** True for re-exports — appears in *our* exports as well. */
  readonly reExported: boolean;
  /**
   * Visibility scope:
   *   - `undefined` → manifest-scoped (visible to every file in the package).
   *   - `Set<fileUri>` → visible only in the listed files. This covers BOTH a
   *     file `use` (the set is the one declaring file) and a **space** `use`
   *     (the set is every file in that space's subtree, {@link scopedToSpace}).
   *
   * `use` in the package-root `package.archspace` is manifest-scoped; `use` in
   * a nested space manifest is space-scoped (subtree files); `use` in a `.arch`
   * file is scoped to that file. If the same name is imported (from the same
   * source package) in multiple scopes, the file URIs accumulate; any
   * package-wide occurrence makes the whole entry package-wide.
   */
  readonly scopedToFiles: ReadonlySet<string> | undefined;
  /**
   * Set only when the import is **space-scoped** (came from a space manifest's
   * `use`) — the declaring space's qualified name, purely for an accurate
   * cross-space visibility message. Dropped on any merge that widens the scope
   * (package/file) or crosses spaces.
   */
  readonly scopedToSpace?: QualifiedName;
}

/**
 * Map of package name (joined with `.`) → resolved package. Used to look
 * up dependencies during resolution.
 */
export type PackageMap = ReadonlyMap<string, ResolvedPackage>;

// =============================================================================
// Resolve a single package
// =============================================================================

/** Result of resolving a package's `use … from …` declarations: the synthetic
 *  imported `TypeDecl`s (to feed the resolver), the import entries (for the
 *  visibility check), and any import diagnostics. */
export interface SynthesizedImports {
  readonly imports: Map<string, ImportEntry>;
  readonly importedTypes: TypeDecl[];
  readonly diagnostics: Diagnostic[];
}

/** A `use`'s visibility scope, normalized. `files === undefined` → package-wide;
 *  a set → visible only in those files; `space` set → that set is a space subtree
 *  (retained for an accurate cross-space diagnostic). */
interface UseScope {
  readonly files: ReadonlySet<string> | undefined;
  readonly space: QualifiedName | undefined;
}

/**
 * Collect every `use` (manifest- and file-scoped), resolve each against the
 * dependency map, and synthesize a `TypeDecl` for each imported kind under its
 * local name. Extracted from `resolvePackage` so the incremental package
 * compiler can recompute imports independently of per-file edits.
 */
export function synthesizeImports(pkg: PackageInput, deps: PackageMap): SynthesizedImports {
  const diagnostics: Diagnostic[] = [];

  // Every file URI in a given space's subtree (§4.11 subtree membership), so a
  // space `use` scopes exactly to the files that space owns.
  const spaceSubtreeFiles = (space: QualifiedName): Set<string> => {
    const out = new Set<string>();
    for (const f of pkg.files) {
      const uri = f.source?.file;
      if (uri === undefined) continue;
      if (spaceContains(space, pkg.spaces?.get(uri))) out.add(uri);
    }
    return out;
  };

  // Three scope kinds, precomputed into a uniform {files, space} descriptor:
  //   - root manifest `use` → package-wide  (`files: undefined`)
  //   - space manifest `use` → the space's subtree file set (+ the space name)
  //   - file `use`          → just that file
  const scopedUses: { use: UseDecl; scope: UseScope }[] = [
    ...(pkg.manifest?.uses ?? []).map((u) => ({
      use: u,
      scope: { files: undefined, space: undefined } as UseScope,
    })),
    ...(pkg.spaceUses ?? []).flatMap((su) =>
      su.uses.map((u) => ({
        use: u,
        scope: { files: spaceSubtreeFiles(su.space), space: su.space } as UseScope,
      }))
    ),
    ...pkg.files.flatMap((f) => {
      const fileUri = f.source?.file;
      return [...f.uses].map((u) => ({
        use: u,
        scope: {
          files: fileUri === undefined ? undefined : new Set([fileUri]),
          space: undefined,
        } as UseScope,
      }));
    }),
  ];

  const imports = new Map<string, ImportEntry>();
  const importedTypes: TypeDecl[] = [];
  // Every (source package, imported kind) pair, so a post-pass can pull each
  // kind's ancestor chain into scope (ancestor closure, below).
  const importedKinds: { sourcePkg: ResolvedPackage; kind: ResolvedType }[] = [];

  for (const { use: u, scope } of scopedUses) {
    const fromName = u.fromPackage.join(".");
    const sourcePkg = deps.get(fromName);
    if (!sourcePkg) {
      diagnostics.push(
        diag(
          "error",
          `Cannot find package '${fromName}'. Add it to dependencies in package.archspace.`,
          u.fromSource ?? u.source,
          "USE_PACKAGE_NOT_FOUND"
        )
      );
      continue;
    }

    if (u.wildcard) {
      for (const [name, kind] of sourcePkg.exports) {
        addImport(imports, importedTypes, diagnostics, name, name, u.fromPackage, kind, u.reExport ?? false, u, scope.files, scope.space);
        importedKinds.push({ sourcePkg, kind });
      }
    } else {
      for (const imp of u.imports) {
        const local = imp.alias ?? imp.name;
        const isAnonymous = !sourcePkg.input.manifest;
        const exported = sourcePkg.exports.get(imp.name);
        const any = lookupAnyType(sourcePkg, imp.name);
        const kind = isAnonymous ? any : exported;
        if (!kind) {
          if (!isAnonymous && any) {
            diagnostics.push(
              diag(
                "error",
                `Type '${imp.name}' exists in package '${fromName}' but is not exported. Add the 'export' keyword to its declaration.`,
                imp.nameSource ?? imp.source,
                "USE_TYPE_NOT_EXPORTED"
              )
            );
          } else {
            diagnostics.push(
              diag("error", `Type '${imp.name}' not found in package '${fromName}'`, imp.nameSource ?? imp.source, "USE_TYPE_NOT_FOUND")
            );
          }
          continue;
        }
        addImport(imports, importedTypes, diagnostics, local, imp.name, u.fromPackage, kind, u.reExport ?? false, u, scope.files, scope.space);
        importedKinds.push({ sourcePkg, kind });
      }
    }
  }

  // Ancestor closure. A synthesized `type <parent> <leaf>` only resolves if
  // <parent> also resolves in this package. When a catalog leaf subtypes a base
  // package's category cross-package (`rds` : `aws_relational` : `relational` :
  // `database`) and only the leaf is imported, its ancestors are neither
  // imported nor locally declared, so the synthetic parent chain dangles
  // (`UNKNOWN_PARENT_TYPE`) and the leaf degrades to a bare module — breaking
  // subtype filters. Pull each imported kind's ancestors in as hidden
  // scaffolding decls (available in the source package's registry even when
  // unexported), named by their real names so the leaf's chain resolves.
  const provided = new Set<string>();
  for (const f of pkg.files) {
    for (const d of f.declarations) {
      if (d.kind === "type") provided.add(d.decl.name);
    }
  }
  for (const t of importedTypes) provided.add(t.name);

  for (const { sourcePkg, kind } of importedKinds) {
    // chain = [base, ...ancestors, immediateParent]; base kinds resolve
    // intrinsically, so skip them and synthesize the rest.
    for (const ancestorName of kind.chain) {
      if (BASE_KIND_NAMES.has(ancestorName) || provided.has(ancestorName)) continue;
      const ancestor = lookupAnyType(sourcePkg, ancestorName);
      if (!ancestor) continue; // Malformed chain — leave it to surface as before.
      importedTypes.push(
        synthesizeTypeDecl(ancestorName, ancestor, sourcePkg.input.name?.join(".") ?? "")
      );
      provided.add(ancestorName);
    }
  }

  return { imports, importedTypes, diagnostics };
}

const BASE_KIND_NAMES: ReadonlySet<string> = new Set([
  "module",
  "surface",
  "interface",
  "process",
  "view",
]);

export function resolvePackage(
  pkg: PackageInput,
  deps: PackageMap
): ResolvedPackage {
  const diagnostics: Diagnostic[] = [];

  // 1-2. Synthesize imported types from this package's `use` declarations
  //      against the dependency map (shared with the incremental compiler).
  const synth = synthesizeImports(pkg, deps);
  diagnostics.push(...synth.diagnostics);
  const imports = synth.imports;
  const importedTypes = synth.importedTypes;

  // 3. Merge imported synthetic TypeDecls into the package's parsed files
  //    so the existing resolver picks them up. We do this by rewriting one
  //    of the files: append the imports as if they were declared locally.
  //
  //    First namespace this package's bare `widget:` tags with its own
  //    prefix (e.g. `boundary` → `arch-c4-boundary`), so the qualified
  //    tag is in place before the cascade stamps it onto instances and
  //    propagates to importing packages. Imported synthetic types keep
  //    whatever tag they were exported with (already qualified at their
  //    origin), so we only qualify the locally-parsed files.
  const localLibrary = pkg.name?.join(".");
  const localFiles = pkg.files.map((f) => qualifyWidgetTags(f, localLibrary));
  const mergedFile: ParsedFile = mergeWithImports(localFiles, importedTypes);
  const result = resolveFile(mergedFile, pkg.spaces);
  diagnostics.push(...result.diagnostics);

  // 4. Visibility check: enforce file-scoped imports. A `use` in a `.arch`
  //    file is only visible inside that file; the registry built above is
  //    intentionally permissive (so each file resolves cleanly when read
  //    in isolation), and this pass catches references in file F to a
  //    kind imported only by some other file.
  checkImportVisibility(pkg, imports, diagnostics);

  // 4b. Cross-space coupling (v0.5). When files live in different spaces
  //     (nested `name:` manifests), a process edge that crosses a space
  //     boundary into a non-`export`ed interface is flagged — soft, because
  //     the model must be able to state coupling that exists in reality.
  if (pkg.spaces && pkg.spaces.size > 0) {
    checkCrossSpaceCoupling(pkg.spaces, result.model, diagnostics);
    checkCrossSpaceTypeVisibility(pkg, pkg.spaces, diagnostics);
  }

  // 5. Compute exports: every locally-declared `export type`, plus every
  //    re-exported import.
  const exports = computeExports(pkg.files, result.model.registry.kinds, imports);

  // 6. Tag every type with its origin library. Synthesized imports
  //    already carry the source package via `synthesizeTypeDecl`;
  //    locally-declared types are tagged here with this package's
  //    name (undefined for anonymous packages). The UI groups the
  //    type browser by this field.
  const taggedTypes = result.model.types.map((t) =>
    t.library !== undefined ? t : { ...t, library: localLibrary }
  );
  const taggedModel = { ...result.model, types: taggedTypes, documents: pkg.documents };

  return {
    input: pkg,
    model: taggedModel,
    diagnostics,
    exports,
    imports,
  };
}

// =============================================================================
// Helpers
// =============================================================================

function addImport(
  imports: Map<string, ImportEntry>,
  importedTypes: TypeDecl[],
  diagnostics: Diagnostic[],
  localName: string,
  originalName: string,
  fromPackage: QualifiedName,
  kind: ResolvedType,
  reExported: boolean,
  use: UseDecl,
  scopeFiles: ReadonlySet<string> | undefined,
  scopeSpace: QualifiedName | undefined
): void {
  const existing = imports.get(localName);
  if (existing) {
    const sameSource =
      existing.originalName === originalName &&
      existing.fromPackage.join(".") === fromPackage.join(".");
    if (!sameSource) {
      diagnostics.push(
        diag(
          "error",
          `Import collision: '${localName}' already imported from '${existing.fromPackage.join(".")}'. Use 'as' to rename.`,
          use.source,
          "USE_NAME_COLLISION"
        )
      );
      return;
    }
    // Same name from same source — accumulate scope (union of visible files). If
    // either occurrence is package-wide (`undefined`), the result is package-wide.
    let merged: ReadonlySet<string> | undefined;
    if (existing.scopedToFiles === undefined || scopeFiles === undefined) {
      merged = undefined;
    } else {
      merged = new Set([...existing.scopedToFiles, ...scopeFiles]);
    }
    // The space tag survives only while the scope stays a single, unchanged
    // space subtree; any widening or cross-space mix falls back to the generic
    // (file-scoped) message.
    const mergedSpace =
      merged !== undefined &&
      existing.scopedToSpace !== undefined &&
      scopeSpace !== undefined &&
      existing.scopedToSpace.join(".") === scopeSpace.join(".")
        ? scopeSpace
        : undefined;
    imports.set(localName, {
      ...existing,
      reExported: existing.reExported || reExported,
      scopedToFiles: merged,
      scopedToSpace: mergedSpace,
    });
    return;
  }
  imports.set(localName, {
    localName,
    originalName,
    fromPackage,
    reExported,
    scopedToFiles: scopeFiles === undefined ? undefined : new Set(scopeFiles),
    scopedToSpace: scopeFiles === undefined ? undefined : scopeSpace,
  });
  // Synthesize a TypeDecl from the imported kind. The local name is the
  // alias or original; the parent kind chain comes from the imported kind.
  importedTypes.push(synthesizeTypeDecl(localName, kind, fromPackage.join(".")));
}

function synthesizeTypeDecl(
  localName: string,
  kind: ResolvedType,
  library: string
): TypeDecl {
  // The immediate parent (last entry in the chain) preserves subtype
  // depth — e.g. an imported `cache` whose chain is `[module, database]`
  // is synthesized as `type database cache`, not `type module cache`,
  // so downstream consumers can still walk the inheritance.
  // Fallback is the base kind, used for kinds that subtype the base
  // directly (chain length 1, where chain[length-1] === baseType).
  const parent = kind.chain[kind.chain.length - 1] ?? kind.baseType;
  return {
    name: localName,
    parentType: parent,
    body: kind.stampedBody,
    exported: false,
    library,
  };
}

function mergeWithImports(
  files: readonly ParsedFile[],
  imports: readonly TypeDecl[]
): ParsedFile {
  const declarations = files.flatMap((f) => [...f.declarations]);
  for (const t of imports) {
    declarations.push({ kind: "type", decl: t });
  }
  const uses = files.flatMap((f) => [...f.uses]);
  return { declarations, uses };
}

function lookupAnyType(
  pkg: ResolvedPackage,
  name: string
): ResolvedType | undefined {
  return pkg.model.registry.kinds.get(name);
}

// =============================================================================
// File-scoped visibility check
// =============================================================================

/**
 * Walk every kind-name reference in every file and verify the import that
 * provides the name is visible from the referencing file. A reference is
 * any occurrence of a kind name as a parent (`type <kind> Foo`), as a
 * declaration head (`<kind> Foo { ... }`), or in a body (surfaces, sub-modules,
 * interfaces). Process and view declarations don't reference type kinds and
 * are skipped here.
 */
function checkImportVisibility(
  pkg: PackageInput,
  imports: ReadonlyMap<string, ImportEntry>,
  diagnostics: Diagnostic[]
): void {
  for (const file of pkg.files) {
    const fileUri = file.source?.file;
    if (fileUri === undefined) continue; // Can't enforce without an identity.

    const check = (name: string, source: SourceSpan | undefined): void => {
      const imp = imports.get(name);
      if (!imp) return; // Not an imported name (base kind, locally declared, or unknown).
      if (imp.scopedToFiles === undefined) return; // Package-wide: visible everywhere.
      if (imp.scopedToFiles.has(fileUri)) return; // This file is in the import's scope.
      const useText = `use ${imp.originalName}${imp.localName !== imp.originalName ? ` as ${imp.localName}` : ""} from ${imp.fromPackage.join(".")}`;
      if (imp.scopedToSpace) {
        // Space-scoped import (§4.11): the `use` lives in another space's manifest.
        const here = pkg.spaces?.get(fileUri);
        diagnostics.push(
          diag(
            "error",
            `Type '${name}' is imported only into space '${imp.scopedToSpace.join(".")}' (via 'use' in that space's package.archspace) and is not visible from ${here ? `space '${here.join(".")}'` : "the package root"}. Add '${useText}' to this space's package.archspace (or the package root), or reference it by qualified path.`,
            source,
            "TYPE_NOT_VISIBLE_IN_FILE"
          )
        );
        return;
      }
      const otherFiles = [...imp.scopedToFiles].sort().join(", ");
      diagnostics.push(
        diag(
          "error",
          `Type '${name}' is not visible in this file. It was imported via 'use' in ${otherFiles}, and 'use' declarations in .arch files are file-scoped. Add '${useText}' here, or move it to package.archspace for package-wide visibility.`,
          source,
          "TYPE_NOT_VISIBLE_IN_FILE"
        )
      );
    };

    for (const decl of file.declarations) {
      checkDeclRefs(decl, check);
    }
  }
}

function checkDeclRefs(
  decl: TopLevelDecl,
  check: (name: string, source: SourceSpan | undefined) => void
): void {
  switch (decl.kind) {
    case "type":
      check(decl.decl.parentType, decl.decl.parentTypeSource);
      checkBodyRefs(decl.decl.body, check);
      return;
    case "module":
      check(decl.decl.typeName, decl.decl.typeSource);
      checkBodyRefs(decl.decl.body, check);
      return;
    case "process":
    case "view":
      // No type-kind references on the wrapper; their inner refs are to
      // module/interface paths (resolved by the validator), not type kinds.
      return;
    case "extension":
      // §4.16: an extension body carries the same type-kind refs as a module body.
      checkBodyRefs(decl.decl.body, check);
      return;
  }
}

function checkBodyRefs(
  body: Body,
  check: (name: string, source: SourceSpan | undefined) => void
): void {
  for (const f of body.surfaces) {
    check(f.typeName, f.typeSource);
    checkBodyRefs(f.body, check);
  }
  for (const i of body.interfaces) {
    check(i.typeName, i.typeSource);
  }
  for (const m of body.modules) {
    check(m.typeName, m.typeSource);
    checkBodyRefs(m.body, check);
  }
}

/**
 * Cross-space TYPE visibility (v0.5, hard). A locally-declared type belongs
 * to the space of its file; referencing it from another space (or from the
 * package root) is an error unless the type is `export`ed. Root-space (no
 * `name:`) types are package-global. Mirrors {@link checkImportVisibility}
 * but for local types across space boundaries, and is hard (a type is
 * genuinely unresolvable across the boundary) — unlike soft coupling.
 */
function checkCrossSpaceTypeVisibility(
  pkg: PackageInput,
  spaces: ReadonlyMap<string, QualifiedName>,
  diagnostics: Diagnostic[]
): void {
  const spaceStr = (file: string | undefined): string | undefined =>
    file !== undefined ? spaces.get(file)?.join(".") : undefined;

  type TypeInfo = { space: string | undefined; exported: boolean };
  const localType = new Map<string, TypeInfo>();
  // Qualified key (`<space>::<name>`) → info, so a qualified parent ref like
  // `Corporate.Loans.Ledger` (v0.5) resolves to the exact declaration.
  const byQualified = new Map<string, TypeInfo>();
  for (const file of pkg.files) {
    for (const decl of file.declarations) {
      if (decl.kind !== "type") continue;
      const space = spaceStr(decl.decl.source?.file);
      const info: TypeInfo = { space, exported: decl.decl.exported === true };
      localType.set(decl.decl.name, info);
      byQualified.set(`${space ?? ""}::${decl.decl.name}`, info);
    }
  }

  for (const file of pkg.files) {
    const here = spaceStr(file.source?.file);
    const check = (name: string, source: SourceSpan | undefined): void => {
      let info: TypeInfo | undefined;
      if (name.includes(".")) {
        const parts = name.split(".");
        const typeName = parts[parts.length - 1]!;
        const spacePath = parts.slice(0, -1).join(".");
        info = byQualified.get(`${spacePath}::${typeName}`);
      } else {
        // Bare ref: resolve over the space chain the way the resolver itself
        // does (local space first, then ancestor spaces, then package root).
        // The flat bare-name map is last-wins, so with two spaces each
        // declaring the same type name it mis-attributed the name to the
        // OTHER space's declaration and hard-errored legal code (r56). The
        // flat map stays as the final fallback so a genuine cross-space ref
        // still errors.
        const chain = here ? here.split(".") : [];
        for (let i = chain.length; i >= 0 && !info; i--) {
          info = byQualified.get(`${chain.slice(0, i).join(".")}::${name}`);
        }
        if (!info) info = localType.get(name);
      }
      if (!info) return; // base / imported / unknown
      if (info.space === undefined) return; // root type — package-global
      if (info.space === here) return; // same space
      if (info.exported) return; // exported — visible cross-space
      diagnostics.push(
        diag(
          "error",
          `Type '${name}' is internal to space '${info.space}' and is not visible from ${here ? `space '${here}'` : "the package root"}. Mark it 'export' to use it across the space boundary.`,
          source,
          "CROSS_SPACE_TYPE_NOT_VISIBLE"
        )
      );
    };
    for (const decl of file.declarations) checkDeclRefs(decl, check);
  }
}

function computeExports(
  files: readonly ParsedFile[],
  kinds: ReadonlyMap<string, ResolvedType>,
  imports: ReadonlyMap<string, ImportEntry>
): ReadonlyMap<string, ResolvedType> {
  const out = new Map<string, ResolvedType>();
  // Locally declared `export type` ...
  for (const f of files) {
    for (const d of f.declarations) {
      if (d.kind !== "type") continue;
      if (!d.decl.exported) continue;
      const kind = kinds.get(d.decl.name);
      if (kind) out.set(d.decl.name, kind);
    }
  }
  // Re-exported imports.
  for (const [name, imp] of imports) {
    if (!imp.reExported) continue;
    const kind = kinds.get(name);
    if (kind) out.set(name, kind);
  }
  return out;
}

// =============================================================================
// Cross-space coupling (v0.5)
// =============================================================================

interface IfaceInfo {
  readonly exported: boolean;
  /** Joined space name, or undefined when the interface is in no space. */
  readonly space: string | undefined;
}

/**
 * Flag process edges that cross a space boundary into a non-`export`ed
 * interface. Soft (warning): the model must be able to *state* coupling that
 * exists in reality, but unsanctioned cross-space dependencies are surfaced
 * so the architect either blesses them (`export`) or removes them.
 */
function checkCrossSpaceCoupling(
  spaces: ReadonlyMap<string, QualifiedName>,
  model: ResolvedModel,
  diagnostics: Diagnostic[]
): void {
  const spaceOf = (file: string | undefined): string | undefined =>
    file !== undefined ? spaces.get(file)?.join(".") : undefined;

  const moduleSpace = new Map<string, string | undefined>();
  const ifaceInfo = new Map<string, IfaceInfo>();
  // v0.8.2 §4.11: references may be written as a hoisted path-SUFFIX, so the
  // coupling check must resolve them the way the edge does — not only by full
  // path. Index modules + interfaces by every suffix, tagged with their space,
  // and resolve a written ref against the referrer's space chain.
  const scopedMods = buildScopedModuleIndex(model);
  const scopedIfaces = new Map<string, ScopedCand<IfaceInfo>[]>();

  const indexIface = (
    iface: ResolvedInterface,
    path: readonly string[],
    moduleFile: string | undefined
  ): void => {
    const file = iface.decl.source?.file ?? moduleFile;
    const info: IfaceInfo = { exported: iface.decl.exported === true, space: spaceOf(file) };
    const full = [...path, iface.name];
    ifaceInfo.set(full.join("."), info);
    // v0.11 (D3): the space-EXTENDED spelling (`Alpha.Api.handle`) is how a ref
    // NAMES another space's twin — so it is precisely the reference D4 exists to
    // soft-flag. Index it too, or the extended form launders an unsanctioned
    // cross-space dependency into silence (the `do` form already flags; the call
    // form must agree). Fill-only: an exact structural path always wins the slot.
    const declSpace = file ? spaces.get(file) : undefined;
    if (declSpace && declSpace.length > 0) {
      const ext = [...declSpace, ...full].join(".");
      if (!ifaceInfo.has(ext)) ifaceInfo.set(ext, info);
    }
    const cand: ScopedCand<IfaceInfo> = {
      value: info,
      path: full,
      space: declSpace,
    };
    for (const key of ifaceSuffixes(path, iface.name)) {
      const list = scopedIfaces.get(key);
      if (list) list.push(cand);
      else scopedIfaces.set(key, [cand]);
    }
  };

  const indexSurface = (
    surface: ResolvedSurface,
    path: readonly string[],
    moduleFile: string | undefined
  ): void => {
    const fpath = [...path, surface.name];
    for (const i of surface.interfaces) indexIface(i, fpath, moduleFile);
    for (const f of surface.surfaces) indexSurface(f, fpath, moduleFile);
  };

  const moduleExported = new Map<string, boolean>();
  const indexModule = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const file = m.decl.source?.file;
    moduleSpace.set(path.join("."), spaceOf(file));
    moduleExported.set(path.join("."), m.decl.exported === true);
    for (const i of m.interfaces) indexIface(i, path, file);
    for (const f of m.surfaces) indexSurface(f, path, file);
    for (const sub of m.modules) indexModule(sub, path);
  };
  for (const m of model.modules) indexModule(m, []);

  const collectSimple = (
    steps: readonly ProcessStep[],
    out: SimpleStep[],
    outDo?: DoStep[]
  ): void => {
    for (const s of steps) {
      switch (s.kind) {
        case "simple":
          out.push(s);
          break;
        case "each":
          collectSimple(s.steps, out, outDo);
          if (s.retry) {
            collectSimple(s.retry.attempt, out, outDo);
            for (const c of s.retry.catches) collectSimple(c.steps, out, outDo);
            if (s.retry.elseSteps) collectSimple(s.retry.elseSteps, out, outDo);
          }
          break;
        case "if":
          for (const b of s.branches) collectSimple(b.steps ?? [], out, outDo);
          if (s.elseSteps) collectSimple(s.elseSteps, out, outDo);
          break;
        case "select":
          for (const c of s.cases) collectSimple(c.steps, out, outDo);
          break;
        case "try":
          collectSimple(s.trySteps, out, outDo);
          for (const c of s.catches) collectSimple(c.steps, out, outDo);
          break;
        case "parallel":
          for (const b of s.branches) collectSimple(b.steps, out, outDo);
          if (s.fanOut) collectSimple(s.fanOut.steps, out, outDo);
          if (s.elseSteps) collectSimple(s.elseSteps, out, outDo); // §2.2 timeout branch
          break;
        case "do":
          outDo?.push(s);
          break; // no interface edge; the splice target is checked separately
        case "note": case "prose": case "go": case "fail": case "finish":
          break; // carry no edge
        case "reversible":
          for (const item of s.items) {
            if (item.forward) {
              if (item.forward.kind === "block") collectSimple(item.forward.steps, out, outDo);
              else collectSimple([item.forward], out, outDo);
            }
            const a = item.unwind?.action;
            if (a?.kind === "simple") out.push(a);
            else if (a?.kind === "do") outDo?.push(a);
            else if (a?.kind === "block") collectSimple(a.steps, out, outDo);
          }
          break;
        case "owner_block": collectSimple(s.steps, out, outDo); break;
        case "await": break;
        default: { const _x: never = s; void _x; }
      }
    }
  };

  const checkSteps = (
    steps: readonly ProcessStep[],
    invoker: { ownerPath?: QualifiedName; space?: QualifiedName },
  ): void => {
    const dos: DoStep[] = [];
    collectSimple(steps, [], dos);
    // v0.11 (D4): a `do` binding another space's non-exported process /
    // subprocess is unsanctioned coupling — same soft posture as the
    // interface check in `checkHopCoupling`. Resolution mirrors the walker/edge scope
    // (buildSpliceScope), so the flag fires exactly on what actually splices.
    if (dos.length > 0) {
      const subScope = buildSpliceScope(model.subprocesses ?? [], invoker);
      const namedProcs = model.processes.filter((p) => !!p.name && !p.anonymous);
      const procScope = buildSpliceScope(
        namedProcs as (typeof namedProcs[number] & { name: string })[],
        invoker,
      );
      const invokerSpaceKey = invoker.space?.join(".");
      for (const d of dos) {
        const key = d.target.join(".");
        const t =
          (subScope.get(key) as { space?: QualifiedName; exported?: boolean } | undefined) ??
          (procScope.get(key) as { space?: QualifiedName; exported?: boolean } | undefined);
        if (!t || !t.space || t.space.length === 0) continue; // unresolved / root-global
        const tKey = t.space.join(".");
        if (tKey === invokerSpaceKey || t.exported) continue;
        diagnostics.push(
          diag(
            "warning",
            `Cross-space coupling: 'do ${key}' targets space '${tKey}', which does not 'export' it. Mark the ${subScope.has(key) ? "subprocess" : "process"} 'export' to sanction this entry point, or route it through an exported gateway.`,
            d.targetSource ?? d.span,
            "CROSS_SPACE_COUPLING"
          )
        );
      }
    }
  };

  /**
   * Interface-edge coupling, read off the resolved IR (§4.13.4): the caller is
   * the hop's RESOLVED one, so an omitted head, a `|` fan and a `\` snake are
   * all attributed to the module that actually performs the call rather than to
   * an empty path (silently skipped) or a mid-chain hop.
   */
  const checkHopCoupling = (body: Flow | undefined): void => {
    forEachCallHop(body, (hop, node) => {
      const source = hop.callee.text.source ?? node.source;
      // The referrer's space is where the step is written; resolve any hoisted
      // suffix against its chain (§4.11), falling back to the full-path index.
      const refSpace = source?.file ? spaces.get(source.file) : undefined;
      const fromKey = hop.caller.join(".");
      if (fromKey.length === 0) return; // unresolvable head — flagged elsewhere
      let callerSpace = moduleSpace.get(fromKey);
      if (callerSpace === undefined) {
        const r = resolveScoped(scopedMods.get(fromKey), refSpace);
        if (r && r !== "ambiguous") callerSpace = spaceOf(r.value.decl.source?.file);
      }
      const toKey = hop.callee.text.text;
      let callee = ifaceInfo.get(toKey);
      if (!callee) {
        const r = resolveScoped(scopedIfaces.get(toKey), refSpace);
        if (r && r !== "ambiguous") callee = r.value;
      }
      if (callerSpace === undefined || !callee || callee.space === undefined) {
        return;
      }
      if (callee.space === callerSpace || callee.exported) return;
      diagnostics.push(
        diag(
          "warning",
          `Cross-space coupling: '${fromKey}' (space '${callerSpace}') uses '${toKey}' in space '${callee.space}', which is not 'export'ed. Mark the interface 'export' to sanction this dependency, or route it through an exported gateway.`,
          source,
          "CROSS_SPACE_COUPLING"
        )
      );
    });
  };

  for (const p of model.processes) checkSteps(p.steps, p);
  for (const sp of model.subprocesses) checkSteps(sp.steps, sp);
  // One source can yield several IR bodies: a type-template subprocess is
  // stamped once per instance. Those bodies are NOT interchangeable — an
  // omitted head resolves to the stamping module, so each instance calls out
  // from its own place, in its own space. Keying on source alone would check
  // whichever instance came first and silently drop the others' violations,
  // making the answer depend on file order. Key on (source, owner) so genuine
  // re-visits of one body collapse and distinct instances each get checked.
  const seenBody = new Set<string>();
  const once = (m: { source?: SourceSpan; ownerPath?: QualifiedName }): boolean => {
    const k = m.source ? `${m.source.file}:${m.source.start.offset}` : undefined;
    if (k === undefined) return true;
    const key = `${k}@${(m.ownerPath ?? []).join(".")}`;
    if (seenBody.has(key)) return false;
    seenBody.add(key);
    return true;
  };
  for (const pm of model.processModels ?? []) if (once(pm)) checkHopCoupling(pm.body);
  for (const sm of model.subprocessModels ?? []) if (once(sm)) checkHopCoupling(sm.body);

  checkAspectRefCoupling();

  /**
   * v0.9.0 §1.3: a membership aspect names a PLACE — coupling to a module, not
   * to an interface — so it gets the same soft treatment as a process edge. A
   * ref reaching into another space's module that is not `export`ed is flagged;
   * `export` on the place blesses it. Cross-PACKAGE refs never get here: a
   * dependency's modules are not in this model, so the ref is already the hard
   * `ASPECT_BAD_REF` (packages never expose instances).
   */
  function checkAspectRefCoupling(): void {
    const visit = (m: ResolvedModule, parent: readonly string[]): void => {
      const path = [...parent, m.name];
      const fromKey = path.join(".");
      const callerSpace = moduleSpace.get(fromKey);
      for (const a of m.decl.body.aspects) {
        const refSpace = a.source?.file ? spaces.get(a.source.file) : undefined;
        for (const it of aspectRefItems(a.value)) {
          const key = it.path.join(".");
          let targetSpace = moduleSpace.get(key);
          let exported = moduleExported.get(key);
          if (targetSpace === undefined && exported === undefined) {
            const r = resolveScoped(scopedMods.get(key), refSpace);
            if (r && r !== "ambiguous") {
              targetSpace = spaceOf(r.value.decl.source?.file);
              exported = r.value.decl.exported === true;
            }
          }
          if (callerSpace === undefined || targetSpace === undefined) continue;
          if (targetSpace === callerSpace || exported) continue;
          diagnostics.push(
            diag(
              "warning",
              `Cross-space coupling: '${fromKey}' (space '${callerSpace}') is placed at '${key}' in space '${targetSpace}', which is not 'export'ed. Mark the place 'export' to sanction this membership, or place the member in its own space.`,
              it.span ?? a.source,
              "CROSS_SPACE_COUPLING"
            )
          );
        }
      }
      for (const c of m.modules) visit(c, path);
    };
    for (const m of model.modules) visit(m, []);
  }
}

/** Atomic ref-valued items of an aspect value, with the span to anchor a
 *  diagnostic. Strings/numbers are classification, never a place reference. */
function aspectRefItems(v: Value | undefined): { path: readonly string[]; span?: SourceSpan }[] {
  if (!v) return [];
  if (v.kind === "identifier") return [{ path: [v.text], span: v.source }];
  if (v.kind === "ref") return [{ path: v.path, span: v.source }];
  if (v.kind === "list") return v.items.flatMap(aspectRefItems);
  return [];
}

// =============================================================================
// Incremental package compiler
// =============================================================================

/** Compiled package state — equivalent to `resolvePackage` + `buildViewerState`.
 *  `model` is cascaded (what the browser renders); `rawModel` is pre-cascade
 *  (what the server serves). Both carry library-tagged types. */
export interface PackageState {
  readonly model: ResolvedModel;
  readonly rawModel: ResolvedModel;
  readonly graph: DependencyGraph;
  readonly diagnostics: readonly Diagnostic[];
}

/** A package file the incremental compiler tracks: raw source + URI. */
export interface PackageFileInput {
  readonly uri: string;
  readonly content: string;
}

function diagSig(d: Diagnostic): string {
  const o = d.source?.start?.offset ?? 0;
  const f = d.source?.file ?? "";
  return `${f} ${o} ${d.code ?? ""} ${d.severity} ${d.message}`;
}

/** Signature of a file's `use` declarations — detects whether an edit changed
 *  the import surface (and thus needs import re-synthesis). */
function usesSig(f: ParsedFile): string {
  return JSON.stringify(f.uses);
}

function dedupeSortDiags(diags: readonly Diagnostic[]): Diagnostic[] {
  // Schwartzian: compute each diag's signature ONCE (during dedup) and sort by
  // the cached key, instead of rebuilding the string up to 3× per comparison in
  // the O(n log n) sort. Output is identical (same dedup, same key order).
  const seen = new Set<string>();
  const tagged: { sig: string; d: Diagnostic }[] = [];
  for (const d of diags) {
    const sig = diagSig(d);
    if (seen.has(sig)) continue;
    seen.add(sig);
    tagged.push({ sig, d });
  }
  tagged.sort((a, b) => (a.sig < b.sig ? -1 : a.sig > b.sig ? 1 : 0));
  return tagged.map((t) => t.d);
}

/**
 * Incremental equivalent of `resolvePackage(...)` + the server's `buildState`.
 *
 * Wraps a per-file {@link IncrementalCompiler}: imports are synthesized once and
 * fed via `setImports` (recomputed only when a `use`/manifest/dep changes); each
 * `.arch` file is fed via `setFile` (the injected parser also namespaces widget
 * tags, matching `qualifyWidgetTags`). A single-file edit re-resolves only the
 * changed declarations. The package-level passes (import-visibility, cross-space)
 * run only when applicable. Produces the same model + diagnostic SET as the cold
 * path — guard with the equivalence harness.
 */
export class IncrementalPackageCompiler {
  private readonly rawParse: ParseFn;
  private deps: PackageMap = new Map();
  private manifest: PackageManifest | undefined;
  private name: QualifiedName | undefined;
  private localLibrary: string | undefined;
  private spaces: ReadonlyMap<string, QualifiedName> | undefined;
  private spaceUses: readonly SpaceScopedUses[] | undefined;
  private rootPath: string | undefined;
  private documents: readonly MarkdownDocument[] = [];

  private ic: IncrementalCompiler;
  private parsed = new Map<string, ParsedFile>();
  private importDiags: readonly Diagnostic[] = [];
  private imports: ReadonlyMap<string, ImportEntry> = new Map();
  private hasFileScopedImports = false;

  constructor(parse: ParseFn) {
    this.rawParse = parse;
    this.ic = this.makeIc();
  }

  /** Fresh inner compiler. `load()` recreates it so a full reseed starts from a
   *  clean slate — no decls lingering from files removed since the last load,
   *  and the new manifest/space context applies to every file. */
  private makeIc(): IncrementalCompiler {
    return new IncrementalCompiler({
      // Parse once, namespace widget tags, and forward parse errors so the
      // engine can surface PARSE_ERROR diagnostics like the cold loaders.
      parse: (content, file) => {
        const r = this.rawParse(content, file);
        return { file: this.qualify(r.file), errors: r.errors };
      },
    });
  }

  private qualify(f: ParsedFile): ParsedFile {
    return qualifyWidgetTags(f, this.localLibrary);
  }

  private pkgInput(): PackageInput {
    return {
      name: this.name,
      manifest: this.manifest,
      files: [...this.parsed.values()],
      rootPath: this.rootPath,
      spaces: this.spaces,
      spaceUses: this.spaceUses,
      documents: this.documents,
    };
  }

  /** Full (re)load: manifest, deps, spaces, and the complete file set. Use on
   *  first load and whenever the manifest / dependency tree / space map changes
   *  (rare). */
  load(opts: {
    name?: QualifiedName;
    manifest?: PackageManifest;
    deps?: PackageMap;
    spaces?: ReadonlyMap<string, QualifiedName>;
    spaceUses?: readonly SpaceScopedUses[];
    rootPath?: string;
    files: readonly PackageFileInput[];
    documents?: readonly MarkdownDocument[];
  }): void {
    this.name = opts.name;
    this.manifest = opts.manifest;
    this.deps = opts.deps ?? new Map();
    this.spaces = opts.spaces;
    this.spaceUses = opts.spaceUses;
    this.rootPath = opts.rootPath;
    this.documents = opts.documents ?? [];
    this.localLibrary = opts.name?.join(".");

    this.ic = this.makeIc(); // clean slate — drop any state from a prior load
    this.parsed.clear();
    for (const f of opts.files) this.parsed.set(f.uri, this.qualify(this.rawParse(f.content, f.uri).file));

    this.ic.setSpaces(this.spaces);
    this.resynthesizeImports();
    for (const f of opts.files) this.ic.setFile(f.uri, f.content);
  }

  /** Incremental single-file edit. Re-parses just this file; re-synthesizes
   *  imports only if its `use` set changed; re-resolves only changed decls.
   *  Returns whether the edit changed the symbol/import surface (structural) —
   *  consumers use it to invalidate identity caches (e.g. serialization). */
  setFile(uri: string, content: string): boolean {
    const next = this.qualify(this.rawParse(content, uri).file);
    const prev = this.parsed.get(uri);
    this.parsed.set(uri, next);
    let structural = false;
    if (!prev || usesSig(prev) !== usesSig(next)) {
      this.resynthesizeImports();
      structural = true;
    }
    const delta = this.ic.setFile(uri, content);
    return structural || delta.structural;
  }

  /** Remove a file from the package. */
  removeFile(uri: string): void {
    const prev = this.parsed.get(uri);
    this.parsed.delete(uri);
    if (prev && usesSig(prev) !== "[]") this.resynthesizeImports();
    this.ic.setFile(uri, ""); // empty content removes it from the engine
  }

  private resynthesizeImports(): void {
    const synth = synthesizeImports(this.pkgInput(), this.deps);
    this.importDiags = synth.diagnostics;
    this.imports = synth.imports;
    this.hasFileScopedImports = [...synth.imports.values()].some((i) => i.scopedToFiles !== undefined);
    this.ic.setImports(synth.importedTypes);
  }

  /** Instrumentation: per-phase wall time (ms) of the last non-cached `state()`
   *  — the inner compiler's split plus this wrapper's extras. Phase-0 only. */
  private ipcTimings: Record<string, number> = {};

  /** Full compiled package state — raw model + graph + merged diagnostics,
   *  equivalent to `resolvePackage` + `buildState`. */
  state(): PackageState {
    const clk = (label: string, fn: () => void): void => {
      const t0 = performance.now();
      fn();
      this.ipcTimings[label] = (this.ipcTimings[label] ?? 0) + (performance.now() - t0);
    };
    this.ipcTimings = {};
    let s!: ReturnType<typeof this.ic.state>;
    clk("ic.state", () => { s = this.ic.state(); });
    // Tag locally-declared types with this package's library (imports already
    // carry their origin). Types are identical in the raw + cascaded models, so
    // tag once and share the array across both.
    let rawModel!: ResolvedModel, model!: ResolvedModel;
    clk("typeTag", () => {
      const taggedTypes = s.rawModel.types.map((t) => (t.library !== undefined ? t : { ...t, library: this.localLibrary }));
      rawModel = { ...s.rawModel, types: taggedTypes };
      model = { ...s.model, types: taggedTypes, documents: this.documents };
    });

    const pkgDiags: Diagnostic[] = [...this.importDiags];
    if (this.hasFileScopedImports) checkImportVisibility(this.pkgInput(), this.imports, pkgDiags);
    if (this.spaces && this.spaces.size > 0) {
      clk("crossSpace", () => {
        checkCrossSpaceCoupling(this.spaces!, rawModel, pkgDiags);
        checkCrossSpaceTypeVisibility(this.pkgInput(), this.spaces!, pkgDiags);
      });
    }
    // Document diagnostics (§4.15) are whole-model, not on the incremental
    // .arch path — run them here so the incremental state matches the cold
    // `validate(model)` which does the same (cold == delta).
    clk("documentDiags", () => documentDiagnostics(model, pkgDiags));

    let diagnostics!: Diagnostic[];
    clk("dedupeSortDiags", () => { diagnostics = dedupeSortDiags([...s.diagnostics, ...pkgDiags]); });
    return { model, rawModel, graph: s.graph, diagnostics };
  }

  /** Instrumentation: per-phase wall time (ms) of the last non-cached `state()`
   *  — the inner compiler's split (`_stateTimings`) merged with this wrapper's
   *  extras (prefixed `ipc:`). Phase-0 attribution of the O(N) residue. */
  _stateTimings(): Record<string, number> {
    const inner = this.ic._stateTimings();
    const out: Record<string, number> = { ...inner };
    for (const [k, v] of Object.entries(this.ipcTimings)) out[`ipc:${k}`] = v;
    return out;
  }
}
